import React, { useEffect, useMemo, useState } from "react";
import "./SuccessReveal.css";

const COLORS = ["#FFD700", "#16b88f", "#4169E1", "#9370DB", "#FF6FA3", "#FBBF24", "#ffffff", "#0b9371"];

// How long the overlay covers the page (iris done → unmount).
const SUCCESS_REVEAL_MS = 2300;

// Replaces the old SuccessAnimation. One flex-centered overlay: the ✓ badge draws,
// a gentle confetti sprinkle falls, then an iris (a circle centered on the badge)
// opens OUTWARD — looney-tunes style — to reveal the order page beneath it.
// Self-contained lifecycle (internal `visible`) so a parent re-render can't cut it short.
export default function SuccessReveal({ onComplete }) {
  const [visible, setVisible] = useState(true);

  const pieces = useMemo(
    () =>
      Array.from({ length: 18 }, (_, i) => {
        const angle = (i / 18) * Math.PI * 2 + (Math.random() - 0.5) * 0.4;
        const dist = 55 + Math.random() * 85; // gentle spread
        const r1 = Math.round(Math.random() * 360);
        return {
          tx: Math.round(Math.cos(angle) * dist), // pure radial -> bursts straight out
          ty: Math.round(Math.sin(angle) * dist),
          r1,
          r2: r1 + 160 + Math.round(Math.random() * 220),
          color: COLORS[i % COLORS.length],
          size: 6 + Math.round(Math.random() * 5),
          delay: Math.round(Math.random() * 220), // wide stagger -> not an explosion
          dur: 1100 + Math.round(Math.random() * 500),
          round: Math.random() > 0.6,
        };
      }),
    []
  );

  useEffect(() => {
    const reveal = setTimeout(() => onComplete && onComplete(), 1450); // iris starts opening
    const off = setTimeout(() => setVisible(false), SUCCESS_REVEAL_MS); // unmount after the iris finishes
    return () => { clearTimeout(reveal); clearTimeout(off); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!visible) return null;

  return (
    <div className="sr-overlay">
      {/* the green plane: a centered circle whose huge box-shadow fills the screen.
          growing the circle opens a hole (the page) outward from the checkmark. */}
      <div className="sr-iris" />

      <div className="sr-confetti">
        {pieces.map((p, i) => (
          <span
            key={i}
            className={`sr-piece${p.round ? " sr-round" : ""}`}
            style={{
              "--tx": `${p.tx}px`,
              "--ty": `${p.ty}px`,
              "--r1": `${p.r1}deg`,
              "--r2": `${p.r2}deg`,
              width: `${p.size}px`,
              height: `${p.round ? p.size : Math.round(p.size * 1.5)}px`,
              background: p.color,
              animationDelay: `${p.delay}ms`,
              animationDuration: `${p.dur}ms`,
            }}
          />
        ))}
      </div>

      <div className="sr-badge">
        <svg className="sr-check" viewBox="0 0 52 52" aria-hidden="true">
          <circle className="sr-ring" cx="26" cy="26" r="24" />
          <path className="sr-tick" d="M15 27l7.5 7.5L37 19" />
        </svg>
      </div>
    </div>
  );
}
